import React, { useState } from 'react';
import ProductNotes, { FragranceNotes, ScentProfileEntry } from './ProductNotes';
import Accordion, { AccordionItem } from '../ui/Accordion';
import { ProductVariant } from '../../data/products';
import './ProductInfo.css';

interface ProductInfoProps {
  title: string;
  description: string;
  type: string;
  variants: ProductVariant[];
  notes?: FragranceNotes;
  scentProfile?: ScentProfileEntry[];
  scent?: string;
  texture?: string;
  badges?: string[];
  accordionItems?: { title: string; content: string }[];
  onAddToCart?: (quantity: number, variant: ProductVariant) => void;
}

const ProductInfo: React.FC<ProductInfoProps> = ({
  title,
  description,
  type,
  variants,
  notes,
  scentProfile,
  scent,
  texture,
  badges = [],
  accordionItems = [],
  onAddToCart,
}) => {
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant>(variants[0]);
  const [quantity, setQuantity] = useState(1);

  const isSingleVariant = variants.length === 1;

  return (
    <div className="product-info">
      <div className="product-info__header">
        <h1 className="product-info__title">{title}</h1>
        <p className="product-info__price">{selectedVariant?.priceDisplay}</p>
      </div>

      <p className="product-info__description">{description}</p>

      {(notes || scent || texture) && (
        <ProductNotes notes={notes} profile={scentProfile} scent={scent} texture={texture} />
      )}

      {/* Size selector:
          - Single variant → show as a plain read-only chip (no interaction needed)
          - Multiple variants → show as interactive selector */}
      <div className="product-info__variants">
        <p className="product-info__section-label">Size</p>
        {isSingleVariant ? (
          <div className="product-info__variant-options">
            <span className="product-info__variant-btn product-info__variant-btn--active product-info__variant-btn--single">
              {variants[0].label}
            </span>
          </div>
        ) : (
          <div className="product-info__variant-options" role="group" aria-label="Select size">
            {variants.map((v) => (
              <button
                key={v.value}
                className={`product-info__variant-btn ${selectedVariant?.value === v.value ? 'product-info__variant-btn--active' : ''}`}
                onClick={() => setSelectedVariant(v)}
                aria-pressed={selectedVariant?.value === v.value}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="product-info__quantity-row">
        <div className="product-info__quantity">
          <p className="product-info__section-label">Qty</p>
          <div className="product-info__qty-controls" role="group" aria-label="Quantity">
            <button
              className="product-info__qty-btn"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              aria-label="Decrease quantity"
            >−</button>
            <span className="product-info__qty-value" aria-live="polite">{quantity}</span>
            <button
              className="product-info__qty-btn"
              onClick={() => setQuantity(quantity + 1)}
              aria-label="Increase quantity"
            >+</button>
          </div>
        </div>
      </div>

      <button
        className="product-info__atc"
        onClick={() => selectedVariant && onAddToCart?.(quantity, selectedVariant)}
      >
        Add to Cart
      </button>

      {badges.length > 0 && (
        <div className="product-info__badges">
          {badges.map((b) => (
            <div key={b} className="product-info__badge">
              <CheckIcon />
              <span>{b}</span>
            </div>
          ))}
        </div>
      )}

      {accordionItems.length > 0 && (
        <Accordion className="product-info__accordion">
          {accordionItems.map((item) => (
            <AccordionItem key={item.title} title={item.title}>
              <p>{item.content}</p>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
};

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default ProductInfo;
